/**
 * MAP-CONTAINER.TSX
 * Plans of the establishment with sites pinned (zoom/pan)
 */
import Card from "@/components/card"
import ContextMenu from "@/components/context-menu"
import ListItem from "@/components/list-item"
import Space from "@/components/space"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import mapStore from "@/entities/map/model/map.store"
import env from "@/env"
import { ReactZoomPanPinchRef, TransformWrapper, TransformComponent } from "react-zoom-pan-pinch"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  faCheck,
  faExpand,
  faLayerGroup,
  faMinus,
  faPen,
  faPlus,
  IconDefinition
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { createEmptySite, Site, SITE_COLOR, SiteGeometryType } from "../../site/model/site.types"
import Tooltip from "@/components/tooltip"
import siteStore from "../../site/model/site.store"
import getPolygonCentroid from "@/utils/get-polygon-centroid.utils"
import {
  geometryToSvgPath,
  getLineMidpoint,
  getPrimaryGeometryPath,
  toMultiLine,
  toMultiPolygon
} from "@/utils/site-geometry.utils"
import SiteAddModal from "../../site/ui/modal/SiteAddModal"
import { FadeIn } from "@/utils/animations.utils"
import MapChip from "./MapChip"
import { getMapImageVersion } from "../model/map-image-cache.utils"
import { store } from "@/index"

type TransformState = { scale: number; positionX: number; positionY: number }
type MapPoint = { x: number; y: number }
type HoverInsertPoint = { afterIndex: number; point: MapPoint }
export type MapEditMode = "site" | "line" | "site-update" | null

const POINT_DISTANCE_PX = 10 // Rayon autour d'un sommet où l'on n'insère pas (réduit => marche sur segments courts)
const SEGMENT_DISTANCE_PX = 20 // Buffer de détection le long d'un segment (élargi => plus facile à viser)
const SNAP_DISTANCE_PX = 14 // Rayon (en px écran) en-deçà duquel un point s'aimante sur un sommet existant
const MAP_PLACEHOLDER_SRC = `data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='800' viewBox='0 0 1200 800'><rect width='1200' height='800' fill='white'/></svg>"
)}`

// Curseur "stylo" (FontAwesome faPen) pour signifier l'édition au survol d'une zone modifiable
const FA_PEN_PATH = Array.isArray(faPen.icon[4]) ? faPen.icon[4][0] : faPen.icon[4]
const EDIT_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 ${faPen.icon[0]} ${faPen.icon[1]}'><path fill='#334155' stroke='#ffffff' stroke-width='28' d='${FA_PEN_PATH}'/></svg>`
)}") 2 20, pointer`

interface OwnProps {
  editAction?: "add-site" | "add-line" | "update-site" | "cancel-edit" | "validate-edit" | "delete-point" | null
  onEditActionHandled?: () => void
  onEditModeChange?: (editMode: MapEditMode) => void
  onEditCanValidateChange?: (canValidate: boolean) => void
  onEditCanDeletePointChange?: (canDeletePoint: boolean) => void
  selectedSite?: Site //Display only one site
  sites?: Site[]
  onSelectSite?: (site: Site) => void
}

export default function MapContainer(props: OwnProps) {
  const dispatch = useAppDispatch()

  //Selectors
  const _session = useAppSelector((state) => state._session)
  const activeMap = useAppSelector(mapStore.selectors.active)
  const maps = useAppSelector(mapStore.selectors.list)
  const isSiteActive = useAppSelector(siteStore.selectors.isActive)
  const selectedSites = useAppSelector(siteStore.selectors.selected)
  const sitesForMap = useAppSelector(siteStore.selectors.listForMap)
  const sites = props.selectedSite ? [props.selectedSite] : (props.sites ?? sitesForMap)
  const renderedSites = [...sites].sort((a, b) => Number(a.geometryType === "line") - Number(b.geometryType === "line"))
  const orderedMaps = [...maps].sort((a, b) => a.order - b.order)

  //References (in order to handle plan / click)
  const clickRef = useRef<{ x: number; y: number } | null>(null) //Detect click position (in order to detect if this is click or pan)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const transformRef = useRef<TransformState>({ scale: 1, positionX: 0, positionY: 0 })
  const viewportRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<ReactZoomPanPinchRef | null>(null)
  //Hauteur du plan quand il est plus haut que la zone : il s'y ajuste, sinon il prend toute la largeur
  const [fitHeight, setFitHeight] = useState<number | null>(null)

  //La taille du plan change apres son chargement : on le recentre
  useEffect(() => {
    wrapperRef.current?.centerView(1, 0)
  }, [fitHeight])
  const suppressClickRef = useRef(false)
  const dragPointIndexRef = useRef<number | null>(null)

  //State
  const [editMode, setEditMode] = useState<MapEditMode>(null) //Switch to editMode
  const [modal, setModal] = useState<null | "MODAL_SITE_ADD">(null)
  const [zoom, setZoom] = useState(1) //Zoom level
  const [polygon, setPolygon] = useState<MapPoint[]>([]) //Polygon to edit
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null)
  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(null)
  const [dragPointIndex, setDragPointIndex] = useState<number | null>(null)
  const [hoverInsertPoint, setHoverInsertPoint] = useState<HoverInsertPoint | null>(null)
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false)
  const [snapPoint, setSnapPoint] = useState<MapPoint | null>(null) //Sommet aimanté sous le curseur
  const [isMapImageUnavailable, setIsMapImageUnavailable] = useState(false)
  const editingSite = sitesForMap.find((site) => site.id === editingSiteId)
  const editingGeometryType: SiteGeometryType =
    editMode === "line" || editingSite?.geometryType === "line" ? "line" : "polygon"
  const minimumPointCount = editingGeometryType === "line" ? 2 : 3

  useEffect(() => {
    props.onEditModeChange?.(editMode)
  }, [editMode, props.onEditModeChange])

  useEffect(() => {
    props.onEditCanValidateChange?.(editMode !== null && polygon.length >= minimumPointCount)
  }, [editMode, minimumPointCount, polygon.length, props.onEditCanValidateChange])

  useEffect(() => {
    props.onEditCanDeletePointChange?.(
      editMode === "site-update" && selectedPointIndex !== null && polygon.length > minimumPointCount
    )
  }, [editMode, minimumPointCount, polygon.length, props.onEditCanDeletePointChange, selectedPointIndex])

  // Detect when active plan is changed
  // Reset edition and reset view
  useEffect(() => {
    setEditMode(null)
    setPolygon([])
    setEditingSiteId(null)
    setSelectedPointIndex(null)
    setDragPointIndex(null)
    setHoverInsertPoint(null)
    setSnapPoint(null)
    setIsLayerMenuOpen(false)
    setModal(null)
    setIsMapImageUnavailable(false)
  }, [activeMap.id])

  useEffect(() => {
    if (!activeMap.id || !props.editAction) return

    if (props.editAction === "add-site") {
      startEdit("polygon")
    } else if (props.editAction === "add-line") {
      startEdit("line")
    } else if (props.editAction === "update-site") {
      startSiteUpdate()
    } else if (props.editAction === "cancel-edit") {
      handleEditEnd()
    } else if (props.editAction === "validate-edit") {
      if (editMode === "site-update") {
        updateSiteGeometry()
      } else if (editMode === "site" || editMode === "line") {
        setModal("MODAL_SITE_ADD")
      }
    } else if (props.editAction === "delete-point") {
      removeSelectedPoint()
    }
    props.onEditActionHandled?.()
  }, [activeMap.id, props.editAction])

  //Init image
  if (!activeMap.id) return null
  const src = `${env.REACT_APP_URL_SPACE}/Maps/${activeMap.id}.png?v=${getMapImageVersion(activeMap.id)}`

  //Draw on the map
  async function addSite(name: string) {
    if (editMode !== "site" && editMode !== "line") return

    const geometryType: SiteGeometryType = editMode === "line" ? "line" : "polygon"

    const response = await dispatch(
      siteStore.thunks.createEntity(
        createEmptySite({
          name,
          geometryType,
          geometry: polygon,
          MapId: activeMap.id
        })
      )
    ).unwrap()

    if (response) {
      if (geometryType === "polygon") {
        // Découpe les zones existantes qui chevauchent la nouvelle
        await dispatch(siteStore.thunks.resolveOverlaps({ topRing: polygon, keepSiteId: response.id }))
      }
      handleEditEnd()
    }
  }

  async function updateSiteGeometry(shouldEndEdit = true) {
    const site = sitesForMap.find((x) => x.id === editingSiteId)
    if (!site || polygon.length < minimumPointCount) return false
    const updatedSite = {
      ...site,
      geometry: polygon
    }

    try {
      dispatch(siteStore.actions.setActive(updatedSite))

      await dispatch(siteStore.thunks.updateEntity()).unwrap()
      dispatch(siteStore.actions.replace(updatedSite))
      if (site.geometryType === "polygon") {
        // Découpe les autres zones qui chevauchent la zone modifiée
        await dispatch(siteStore.thunks.resolveOverlaps({ topRing: polygon, keepSiteId: site.id })).unwrap()
      }
    } catch {
      return false
    }

    if (shouldEndEdit) handleEditEnd()
    return true
  }

  //End of edition
  function handleEditEnd() {
    setEditMode(null)
    setPolygon([])
    setEditingSiteId(null)
    setSelectedPointIndex(null)
    setDragPointIndex(null)
    setHoverInsertPoint(null)
    setSnapPoint(null)
    dragPointIndexRef.current = null
    setIsLayerMenuOpen(false)
    setModal(null)
    // On ne veut pas ouvrir le dashboard du site à la fin de l'édition
    dispatch(siteStore.actions.setActive(null))
    dispatch(siteStore.actions.select([]))
  }

  function getPointFromPointer(e: React.PointerEvent<HTMLElement>): MapPoint | null {
    if (!imgRef.current || !viewportRef.current) return null

    const { scale, positionX, positionY } = transformRef.current
    const viewportRect = viewportRef.current.getBoundingClientRect()
    const clientX = e.clientX - viewportRect.left
    const clientY = e.clientY - viewportRect.top

    const w = imgRef.current.offsetWidth
    const h = imgRef.current.offsetHeight
    if (!w || !h) return null

    const xInContentPx = (clientX - positionX) / scale
    const yInContentPx = (clientY - positionY) / scale

    return {
      x: Math.min(1, Math.max(0, xInContentPx / w)),
      y: Math.min(1, Math.max(0, yInContentPx / h))
    }
  }

  function getDistancePx(a: MapPoint, b: MapPoint) {
    const w = imgRef.current?.offsetWidth ?? 1
    const h = imgRef.current?.offsetHeight ?? 1
    return Math.hypot((a.x - b.x) * w, (a.y - b.y) * h)
  }

  function getClosestPointOnSegment(point: MapPoint, start: MapPoint, end: MapPoint) {
    const w = imgRef.current?.offsetWidth ?? 1
    const h = imgRef.current?.offsetHeight ?? 1
    const pointPx = { x: point.x * w, y: point.y * h }
    const startPx = { x: start.x * w, y: start.y * h }
    const endPx = { x: end.x * w, y: end.y * h }
    const dx = endPx.x - startPx.x
    const dy = endPx.y - startPx.y
    const segmentLength = dx * dx + dy * dy
    const ratio = segmentLength
      ? Math.min(1, Math.max(0, ((pointPx.x - startPx.x) * dx + (pointPx.y - startPx.y) * dy) / segmentLength))
      : 0

    return {
      x: (startPx.x + dx * ratio) / w,
      y: (startPx.y + dy * ratio) / h
    }
  }

  function getHoverInsertPoint(point: MapPoint): HoverInsertPoint | null {
    if (editMode !== "site-update" || !editingSiteId || polygon.length < minimumPointCount) return null

    const isNearExistingPoint = polygon.some((p) => getDistancePx(point, p) < POINT_DISTANCE_PX)
    if (isNearExistingPoint) return null

    let closest: HoverInsertPoint | null = null
    let closestDistance = SEGMENT_DISTANCE_PX

    const segmentCount = editingGeometryType === "line" ? polygon.length - 1 : polygon.length
    polygon.slice(0, segmentCount).forEach((p, index) => {
      const nextPoint = polygon[(index + 1) % polygon.length]
      const candidate = getClosestPointOnSegment(point, p, nextPoint)
      const distance = getDistancePx(point, candidate)

      if (distance < closestDistance) {
        closestDistance = distance
        closest = { afterIndex: index, point: candidate }
      }
    })

    return closest
  }

  // Sommets ET arêtes aimantables des autres zones de la map
  const { snapTargets, snapSegments } = useMemo(() => {
    const points: MapPoint[] = []
    const segments: { a: MapPoint; b: MapPoint }[] = []
    for (const site of sitesForMap) {
      if (editMode === "site-update" && site.id === editingSiteId) continue
      const paths =
        site.geometryType === "line"
          ? toMultiLine(site.geometry).map((line) => ({ points: line, closed: false }))
          : toMultiPolygon(site.geometry).flatMap((poly) => poly.map((ring) => ({ points: ring, closed: true })))

      for (const path of paths) {
        path.points.forEach((p, index) => {
          points.push({ x: p.x, y: p.y })
          if (path.closed || index < path.points.length - 1) {
            segments.push({ a: p, b: path.points[(index + 1) % path.points.length] })
          }
        })
      }
    }
    return { snapTargets: points, snapSegments: segments }
  }, [sitesForMap, editMode, editingSiteId])

  // Cherche le point d'aimantation le plus proche dans le rayon de snap.
  // Priorité aux sommets (zones existantes + polygone en cours) ; sinon projection sur une arête.
  function getSnapPoint(point: MapPoint, excludeIndex: number | null = null): MapPoint | null {
    const threshold = SNAP_DISTANCE_PX / (zoom || 1) // rayon constant à l'écran

    // 1) Sommets (priorité)
    let bestVertex: MapPoint | null = null
    let bestVertexDistance = threshold

    for (const target of snapTargets) {
      const distance = getDistancePx(point, target)
      if (distance < bestVertexDistance) {
        bestVertexDistance = distance
        bestVertex = target
      }
    }
    polygon.forEach((p, index) => {
      if (index === excludeIndex) return
      const distance = getDistancePx(point, p)
      if (distance < bestVertexDistance) {
        bestVertexDistance = distance
        bestVertex = p
      }
    })
    if (bestVertex) return bestVertex

    // 2) Arêtes des zones existantes (projection sur le segment)
    let bestEdge: MapPoint | null = null
    let bestEdgeDistance = threshold
    for (const segment of snapSegments) {
      const candidate = getClosestPointOnSegment(point, segment.a, segment.b)
      const distance = getDistancePx(point, candidate)
      if (distance < bestEdgeDistance) {
        bestEdgeDistance = distance
        bestEdge = candidate
      }
    }
    return bestEdge
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const point = getPointFromPointer(e)
    if (!point) return

    const currentDragPointIndex = dragPointIndexRef.current
    if (currentDragPointIndex !== null) {
      e.preventDefault()
      const snap = getSnapPoint(point, currentDragPointIndex)
      const finalPoint = snap ?? point
      setPolygon((prev) => prev.map((p, index) => (index === currentDragPointIndex ? finalPoint : p)))
      setSnapPoint(snap)
      setHoverInsertPoint(null)
      return
    }

    if (editMode === "site" || editMode === "line") {
      setSnapPoint(getSnapPoint(point))
      return
    }

    setHoverInsertPoint(getHoverInsertPoint(point))
  }

  //Handle action on the plan
  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (dragPointIndexRef.current !== null) {
      dragPointIndexRef.current = null
      setDragPointIndex(null)
      setSnapPoint(null)
      clickRef.current = null
      suppressClickRef.current = true
      requestAnimationFrame(() => {
        suppressClickRef.current = false
      })
      return
    }

    const c = clickRef.current
    if (!c) return
    const clickX = e.clientX - c.x
    const clickY = e.clientY - c.y
    const isMoved = clickX * clickX + clickY * clickY > 200
    clickRef.current = null

    // Si déplacement => pan, on bloque le click qui suit
    if (isMoved) {
      suppressClickRef.current = true
      requestAnimationFrame(() => {
        suppressClickRef.current = false
      })
      return
    }

    if (editMode !== "site" && editMode !== "line") return

    const point = getPointFromPointer(e)
    if (!point) return

    // Aimante le nouveau point sur un sommet existant si on est assez proche
    const snapped = getSnapPoint(point) ?? point
    setPolygon((prev) => [...prev, snapped])
    setSnapPoint(null)
  }

  //Handle start clicking
  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    clickRef.current = { x: e.clientX, y: e.clientY }
  }

  //Click on site
  async function selectSite(e: React.MouseEvent<HTMLElement | SVGElement>, site: Site) {
    e.stopPropagation()

    if (suppressClickRef.current) return

    if (editMode === "site-update") {
      if (site.id === editingSiteId) return

      if (editingSiteId && !(await updateSiteGeometry(false))) return

      //La sauvegarde peut avoir decoupe la zone suivante : repartir de sa geometrie a jour.
      const nextSite = siteStore.selectors.listForMap(store.getState()).find((item) => item.id === site.id)
      if (!nextSite) return

      setEditingSiteId(nextSite.id)
      // On édite le chemin principal (la géométrie redevient simple à la sauvegarde)
      setPolygon(getPrimaryGeometryPath(nextSite.geometryType, nextSite.geometry))
      setSelectedPointIndex(null)
      setDragPointIndex(null)
      setHoverInsertPoint(null)
      dispatch(siteStore.actions.select([nextSite.id]))
      return
    }

    if (editMode) return

    if (props.onSelectSite) {
      props.onSelectSite(site)
    } else if (!props.selectedSite) {
      dispatch(siteStore.thunks.select(site))
    }
  }

  //Start edit
  function startEdit(geometryType: SiteGeometryType) {
    setEditMode(geometryType === "line" ? "line" : "site")
    setPolygon([])
    setEditingSiteId(null)
    setSelectedPointIndex(null)
    setDragPointIndex(null)
    setHoverInsertPoint(null)
    setSnapPoint(null)
    dragPointIndexRef.current = null
    setModal(null)
  }

  function startSiteUpdate() {
    setEditMode("site-update")
    setPolygon([])
    setEditingSiteId(null)
    setSelectedPointIndex(null)
    setDragPointIndex(null)
    setHoverInsertPoint(null)
    setSnapPoint(null)
    dragPointIndexRef.current = null
    setModal(null)
  }

  function insertPoint() {
    if (!hoverInsertPoint) return
    setPolygon((prev) => [
      ...prev.slice(0, hoverInsertPoint.afterIndex + 1),
      hoverInsertPoint.point,
      ...prev.slice(hoverInsertPoint.afterIndex + 1)
    ])
    setSelectedPointIndex(hoverInsertPoint.afterIndex + 1)
    setHoverInsertPoint(null)
  }

  function removeSelectedPoint() {
    if (selectedPointIndex === null || polygon.length <= minimumPointCount) return
    setPolygon((prev) => prev.filter((_, index) => index !== selectedPointIndex))
    setSelectedPointIndex(null)
  }

  function selectMapLayer(mapId: string) {
    const map = orderedMaps.find((x) => x.id === mapId)
    if (!map) return
    if (map.id === activeMap.id) {
      setIsLayerMenuOpen(false)
      return
    }

    handleEditEnd()
    dispatch(mapStore.actions.setActive(map))
    dispatch(siteStore.actions.setActive(null))
    setIsLayerMenuOpen(false)
  }

  function ControlButton(icon: IconDefinition, onClick: () => void, text: string) {
    return (
      <Tooltip text={text} position="left" key={text}>
        <button
          type="button"
          onClick={() => onClick()}
          className="h-9 w-9 cursor-pointer rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50">
          <FontAwesomeIcon icon={icon} />
        </button>
      </Tooltip>
    )
  }

  //Plan plus haut que la zone d'affichage : il s'ajuste a sa hauteur pour etre vu en entier a l'arrivee
  function fitImage() {
    const img = imgRef.current
    const viewport = viewportRef.current
    if (!img?.naturalWidth || !viewport) return
    const isTooTall = viewport.clientWidth * (img.naturalHeight / img.naturalWidth) > viewport.clientHeight
    setFitHeight(isTooTall ? viewport.clientHeight : null)
  }

  //Map icon
  function MapPin({ x, y, color, name, zoom }: { x: number; y: number; color: string; name: string; zoom: number }) {
    const inv = 1 / (zoom || 1)

    const Wrapper = selectedSites.length ? "div" : FadeIn

    return (
      <Wrapper
        className="absolute z-20 cursor-pointer select-none pointer-events-none"
        style={{
          left: `${x * 100}%`,
          top: `${y * 100}%`,
          transform: `translate(-50%, -100%) scale(${inv})`,
          transformOrigin: "50% 100%"
        }}>
        <div className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2">
          <MapChip color={color} name={name} />
        </div>
      </Wrapper>
    )
  }

  return (
    <Card
      isWithoutPadding
      className={`relative mb-0 flex-1 ${props.selectedSite ? "box-border overflow-hidden" : ""}`}
      isFullWidth
      style={props.selectedSite ? { border: "1px solid lightgrey" } : undefined}>
      {modal === "MODAL_SITE_ADD" && editMode && (
        <SiteAddModal
          title={editMode === "line" ? "Ajouter une circulation" : "Ajouter une zone de travail"}
          inputTitle={editMode === "line" ? "Nom de la circulation" : "Nom du lieu"}
          initialName={editMode === "line" ? "Circulation" : ""}
          onNext={(name) => addSite(name)}
          onClose={() => handleEditEnd()}
        />
      )}

      {editMode && (
        <div
          className="-right-[1px] absolute text-sm py-1 px-3 z-50"
          style={{
            backgroundColor: SITE_COLOR,
            color: "white",
            borderRadius: "0px 0px 0px 12px"
          }}>
          {editMode === "site-update" ? (
            <span>
              {editingSite
                ? `Modifiez les points de ${editingSite.geometryType === "line" ? "la circulation" : "la zone"}`
                : "Cliquez sur un lieu de travail pour modifier sa géométrie"}
            </span>
          ) : polygon.length >= minimumPointCount ? (
            <span>
              Cliquez sur <FontAwesomeIcon icon={faCheck} /> pour valider
            </span>
          ) : (
            <span>
              Cliquez dans la carte pour ajouter {editMode === "line" ? "une circulation" : "une zone de travail"}
            </span>
          )}
        </div>
      )}

      <div
        className="absolute h-full w-full min-w-0 overflow-hidden rounded-[12px] bg-white"
        style={{ border: props.selectedSite ? "none" : `1px solid ${editMode ? SITE_COLOR : "lightgrey"}` }}>
        <TransformWrapper
          ref={wrapperRef}
          key={activeMap.id}
          initialScale={1}
          minScale={0.5}
          maxScale={6}
          centerOnInit
          wheel={{ step: 0.12 }}
          pinch={{ step: 5 }}
          doubleClick={{ mode: "zoomIn" }}
          panning={{ velocityDisabled: true, disabled: dragPointIndex !== null }}
          onTransformed={(_, state) => {
            transformRef.current = {
              scale: state.scale,
              positionX: state.positionX,
              positionY: state.positionY
            }
            setZoom(state.scale)
          }}>
          {({ zoomIn, zoomOut, centerView }) => {
            return (
              <>
                {(!isSiteActive || editMode) && (
                  <div className="absolute left-3 top-3 z-10 flex flex-col gap-2">
                    {ControlButton(faPlus, zoomIn, "Zoom avant")}
                    {ControlButton(faMinus, zoomOut, "Zoom arrière")}
                    {!props.selectedSite && orderedMaps.length > 1 && (
                      <div className="relative">
                        {ControlButton(faLayerGroup, () => setIsLayerMenuOpen((prev) => !prev), "Changer de niveau")}

                        {isLayerMenuOpen && (
                          <ContextMenu onClose={() => setIsLayerMenuOpen(false)}>
                            {orderedMaps.map((map) => {
                              const isActive = map.id === activeMap.id

                              return (
                                <ListItem
                                  key={map.id}
                                  isEditable
                                  isSmall
                                  isActive={isActive}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    selectMapLayer(map.id)
                                  }}>
                                  <span className="whitespace-nowrap">{map.name}</span>
                                  <Space />
                                  {isActive && <FontAwesomeIcon icon={faCheck} />}
                                </ListItem>
                              )
                            })}
                          </ContextMenu>
                        )}
                      </div>
                    )}
                    {ControlButton(
                      faExpand,
                      () => {
                        centerView(1)
                        dispatch(siteStore.actions.setActive(null))
                      },
                      "Retour à la vue initiale"
                    )}
                  </div>
                )}

                <div
                  ref={viewportRef}
                  className="h-full w-full"
                  onPointerUp={handlePointerUp}
                  onPointerMove={handlePointerMove}
                  onPointerDown={handlePointerDown}>
                  <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }}>
                    <div className={`relative ${fitHeight ? "w-fit" : ""}`}>
                      <img
                        ref={imgRef}
                        src={isMapImageUnavailable ? MAP_PLACEHOLDER_SRC : src}
                        alt={activeMap.name}
                        draggable={false}
                        className={`select-none pointer-events-none block grayscale opacity-40 ${fitHeight ? "w-auto" : "w-full h-auto"}`}
                        style={fitHeight ? { height: fitHeight } : undefined}
                        onLoad={fitImage}
                        onError={() => setIsMapImageUnavailable(true)}
                      />

                      {isMapImageUnavailable && (
                        <div className="absolute inset-0 grid place-items-center pointer-events-none grey-t opacity-50">
                          Plan indisponible
                        </div>
                      )}

                      {polygon.length > 1 && (
                        <svg
                          className="absolute left-0 top-0 h-full w-full z-10 pointer-events-none"
                          viewBox="0 0 1 1"
                          preserveAspectRatio="none">
                          {editingGeometryType === "line" ? (
                            <polyline
                              points={polygon.map((p) => `${p.x},${p.y}`).join(" ")}
                              fill="none"
                              stroke={SITE_COLOR}
                              strokeWidth={5}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              vectorEffect="non-scaling-stroke"
                            />
                          ) : (
                            <polygon
                              points={polygon.map((p) => `${p.x},${p.y}`).join(" ")}
                              fill={SITE_COLOR}
                              fillOpacity={0.3}
                              stroke={SITE_COLOR}
                              strokeWidth={2}
                              vectorEffect="non-scaling-stroke"
                            />
                          )}
                        </svg>
                      )}

                      {(editMode === "site" || editMode === "line") &&
                        polygon.map((point, index) => (
                          <div
                            key={`draft-point-${index}`}
                            className="absolute z-20 h-3 w-3 rounded-full pointer-events-none"
                            style={{
                              left: `${point.x * 100}%`,
                              top: `${point.y * 100}%`,
                              backgroundColor: SITE_COLOR,
                              transform: `translate(-50%, -50%) scale(${1 / (zoom || 1)})`
                            }}
                          />
                        ))}

                      {/* Points d'édition (divs HTML => ronds quelque soit le ratio de l'image) */}
                      {editMode === "site-update" &&
                        polygon.map((p, index) => {
                          const isSelected = selectedPointIndex === index
                          const isDragging = dragPointIndex === index
                          return (
                            <div
                              key={`${index}`}
                              className="absolute z-20"
                              style={{
                                left: `${p.x * 100}%`,
                                top: `${p.y * 100}%`,
                                transform: `translate(-50%, -50%) scale(${1 / (zoom || 1)})`,
                                touchAction: "none",
                                cursor: isDragging ? "grabbing" : "grab"
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedPointIndex(index)
                              }}
                              onPointerDown={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                e.currentTarget.setPointerCapture(e.pointerId)
                                dragPointIndexRef.current = index
                                setSelectedPointIndex(index)
                                setDragPointIndex(index)
                              }}>
                              <div
                                className="grid place-items-center rounded-full transition-[height,width]"
                                style={{
                                  height: isSelected ? 16 : 12,
                                  width: isSelected ? 16 : 12,
                                  backgroundColor: isSelected ? _session.accountColors.cta : SITE_COLOR
                                }}>
                                {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                              </div>
                            </div>
                          )
                        })}

                      {/* Point fantôme d'insertion sur un segment */}
                      {editMode === "site-update" && hoverInsertPoint && dragPointIndex === null && (
                        <div
                          className="absolute z-20 grid place-items-center"
                          style={{
                            left: `${hoverInsertPoint.point.x * 100}%`,
                            top: `${hoverInsertPoint.point.y * 100}%`,
                            height: 30,
                            width: 30,
                            transform: `translate(-50%, -50%) scale(${1 / (zoom || 1)})`,
                            touchAction: "none",
                            cursor: EDIT_CURSOR
                          }}
                          onPointerDown={(e) => {
                            e.stopPropagation()
                            e.preventDefault()
                            insertPoint()
                          }}>
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{
                              backgroundColor: _session.accountColors.cta,
                              opacity: 0.9
                            }}
                          />
                        </div>
                      )}

                      {/* Indicateur d'aimantation (snapping) sur un sommet existant */}
                      {snapPoint && (editMode === "site" || editMode === "line" || dragPointIndex !== null) && (
                        <div
                          className="absolute z-30 grid place-items-center pointer-events-none"
                          style={{
                            left: `${snapPoint.x * 100}%`,
                            top: `${snapPoint.y * 100}%`,
                            height: 22,
                            width: 22,
                            transform: `translate(-50%, -50%) scale(${1 / (zoom || 1)})`
                          }}>
                          <div
                            className="grid h-full w-full place-items-center rounded-full"
                            style={{
                              border: `2px solid ${_session.accountColors.cta}`,
                              backgroundColor: "rgba(255,255,255,0.6)"
                            }}>
                            <div
                              className="h-2 w-2 rounded-full"
                              style={{ backgroundColor: _session.accountColors.cta }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Tous les polygons existants dans un seul SVG */}
                      <svg
                        className="absolute left-0 top-0 h-full w-full z-0"
                        viewBox="0 0 1 1"
                        preserveAspectRatio="none">
                        {renderedSites.map((site) => {
                          if (site.id === editingSiteId) return null
                          const isInactive = selectedSites.length && selectedSites[0] !== site.id
                          return (
                            <path
                              onMouseEnter={() => {
                                if (props.selectedSite) return
                                // Hover autorisé hors édition ET en mode modification de zone (curseur stylo)
                                if (editMode && editMode !== "site-update") return
                                if (selectedSites[0] !== site.id) {
                                  dispatch(siteStore.actions.select(site.id))
                                }
                              }}
                              onMouseLeave={() => {
                                if (props.selectedSite) return
                                if (!editMode || editMode === "site-update") dispatch(siteStore.actions.select([]))
                              }}
                              key={site.id}
                              d={geometryToSvgPath(site.geometryType, site.geometry)}
                              fillRule="evenodd"
                              fill={site.geometryType === "line" ? "none" : site.cColor}
                              fillOpacity={site.geometryType === "line" ? 0 : 0.25}
                              stroke={site.geometryType === "line" ? site.cColor : SITE_COLOR}
                              strokeWidth={
                                site.geometryType === "line"
                                  ? isInactive || !selectedSites.length || props.selectedSite
                                    ? 5
                                    : 7
                                  : isInactive || !selectedSites.length || props.selectedSite
                                    ? 1
                                    : 3
                              }
                              strokeLinecap={site.geometryType === "line" ? "round" : undefined}
                              strokeLinejoin={site.geometryType === "line" ? "round" : undefined}
                              vectorEffect="non-scaling-stroke"
                              onClick={(e) => selectSite(e, site)}
                              style={{
                                pointerEvents: site.geometryType === "line" ? "stroke" : "fill",
                                cursor: editMode === "site-update" ? EDIT_CURSOR : "pointer"
                              }}
                            />
                          )
                        })}
                      </svg>

                      {/* Site names */}
                      {!props.selectedSite &&
                        renderedSites.map((site) => {
                          const isInactive = selectedSites.length && selectedSites[0] !== site.id
                          if (isInactive) return null
                          const { x, y } =
                            site.geometryType === "line"
                              ? getLineMidpoint(site.geometry)
                              : getPolygonCentroid(getPrimaryGeometryPath("polygon", site.geometry))
                          return <MapPin key={site.id} x={x} y={y} color={site.cColor} name={site.name} zoom={zoom} />
                        })}
                    </div>
                  </TransformComponent>
                </div>
              </>
            )
          }}
        </TransformWrapper>
      </div>
    </Card>
  )
}
